import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container"]
  static values = { 
    listId: String,
    tasksCount: Number  
  }

  connect() {
    this.celebrationShown = false;
    this.observer = null;
    this.setupObserver();
  }

  disconnect() {
    this.cleanupObserver();
  }

  setupObserver() {
    if (!this.hasContainerTarget) return;
    
    this.observer = new MutationObserver(() => {
      this.debounceCheckCompletion();
    });

    this.observer.observe(this.containerTarget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-task-status']
    });
  }

  cleanupObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  debounceCheckCompletion() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.checkCompletion();
    }, 300);
  }

  checkCompletion() {
    if (this.celebrationShown) return;

    const tasks = this.containerTarget.querySelectorAll('[data-task-status]');
    const totalTasks = tasks.length;

    if (totalTasks === 0) return;
    if (totalTasks !== this.tasksCountValue) return; // Wait for all tasks to load

    const completedTasks = Array.from(tasks).filter(
      task => task.dataset.taskStatus === "done"
    );

    if (completedTasks.length === totalTasks) {
      this.celebrationShown = true;
      this.celebrate();
    }
  }

  celebrate() {
    this.addCompletionStyling();
    this.showToast();
    this.createConfetti();
  }

  addCompletionStyling() {
    this.element.classList.add('list-completed');
    this.element.style.animation = 'celebration-glow 2s ease-out';
    
    setTimeout(() => {
      this.element.style.animation = '';
    }, 2000);
  }

  showToast() {
    const toast = document.createElement('div');
    toast.className = `
      fixed top-4 right-4 z-[100] 
      bg-emerald-500 text-white rounded-xl shadow-2xl
      transform transition-all duration-300
      animate-slide-in-right
    `;
    
    toast.innerHTML = `
      <div class="flex items-center gap-3 p-4">
        <div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="min-w-0">
          <p class="font-semibold text-sm">List completed! 🎉</p>
          <p class="text-xs text-white/80">All tasks have been finished</p>
        </div>
        <button class="ml-2 p-1 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                onclick="this.parentElement.remove()"
                aria-label="Dismiss">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  createConfetti() {
    const colors = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
    const fragment = document.createDocumentFragment();
    const container = document.createElement('div');
    
    container.className = 'fixed inset-0 pointer-events-none z-[99]';
    
    for (let i = 0; i < 30; i++) {
      const confetti = document.createElement('div');
      const color = colors[Math.floor(Math.random() * colors.length)];
      const left = Math.random() * 100;
      const delay = Math.random() * 0.5;
      const size = Math.random() * 8 + 4;
      const rotation = Math.random() * 360;
      
      confetti.style.cssText = `
        position: absolute;
        width: ${size}px;
        height: ${size * 0.6}px;
        background: ${color};
        left: ${left}%;
        top: -5%;
        border-radius: 2px;
        opacity: 0;
        animation: confetti-fall ${2 + Math.random() * 2}s ease-in ${delay}s forwards;
        transform: rotate(${rotation}deg);
      `;
      
      fragment.appendChild(confetti);
    }
    
    container.appendChild(fragment);
    document.body.appendChild(container);
    
    setTimeout(() => {
      container.style.opacity = '0';
      container.style.transition = 'opacity 0.5s';
      setTimeout(() => container.remove(), 500);
    }, 3500);
  }
}